import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TicketClosingFieldStorageEntry } from '../models/ticket.model';

/**
 * Persiste en localStorage (key `ticketClosingFields`) los valores/archivos capturados en los
 * formularios dinámicos y evidencias, identificados por `code` (closing field). Es el único punto
 * de acceso a ese storage; los servicios de validación (`ValidationLocationService`,
 * `ValidationWorkflowProjectService`, `TicketCloseService`) lo leen para armar los
 * payloads de cierre, y las pantallas de captura lo escriben mientras el usuario avanza.
 */
@Injectable({
  providedIn: 'root',
})
export class TicketClosingFieldsStorageService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly storageKey = 'ticketClosingFields';
  private inMemoryEntries: Map<string, TicketClosingFieldStorageEntry> | null = null;

  /** Guarda el valor de un campo de texto/número/booleano. Usado por: asset-conditions.ts, maintenance.ts, asset-evidence.ts, questionnaire-completed.ts, home.component.ts. */
  public setValue(code: string, value: string | number | boolean | null, remarks = ''): void {
    this.upsert(code, { value, remarks });
  }

  /** Guarda un archivo (evidencia) en base64. Usado por: abroad.ts, biometrics.ts, official-id.ts. */
  public setFile(code: string, fileName: string, fileContent: string): void {
    this.upsert(code, { fileName, fileContent });
  }

  /** Devuelve todas las entradas guardadas. Usado por: ticket-close.service.ts (arma el payload de cierre). */
  public getAll(): TicketClosingFieldStorageEntry[] {
    return Array.from(this.getEntries().values());
  }

  /** Busca una entrada por `code`. Uso interno de este servicio (base de getStoredString/getStoredNumber). */
  public getByCode(code: string): TicketClosingFieldStorageEntry | undefined {
    return this.getEntries().get(code);
  }

  /** Valor guardado para `code` como texto recortado, o '' si no existe/no es texto/número. Usado por: ensure-location.service.ts, ticket-create.service.ts. */
  public getStoredString(code: string): string {
    return this.toTrimmedString(this.getByCode(code)?.value);
  }

  /** Valor guardado para `code` como número entero positivo, o null si no aplica. Usado por: ensure-location.service.ts, ticket-create.service.ts. */
  public getStoredNumber(code: string): number | null {
    const value = this.getByCode(code)?.value;

    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }

    if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
      return Number(value.trim());
    }

    return null;
  }

  /** Normaliza cualquier valor a texto recortado; números se formatean, el resto cae en ''. Usado por: ensure-location.service.ts, ticket-create.service.ts (además de uso interno). */
  public toTrimmedString(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${value}`;
    }

    return '';
  }

  /** Borra todo el storage de cierre. Usado por: verification-completed.ts (al finalizar el flujo). */
  public clear(): void {
    if (!this.isBrowser) {
      return;
    }

    this.inMemoryEntries = null;
    localStorage.removeItem(this.storageKey);
  }

  /** Combina `patch` con la entrada existente (o una por defecto) y persiste. Uso interno: setValue/setFile. */
  private upsert(code: string, patch: Partial<Omit<TicketClosingFieldStorageEntry, 'code'>>): void {
    if (!this.isBrowser) {
      return;
    }

    const entries = this.getEntries();
    const currentEntry = entries.get(code) ?? this.createDefaultEntry(code);
    entries.set(code, { ...currentEntry, ...patch });
    this.persist(entries);
  }

  /** Entrada vacía para `code` cuando aún no existe en storage. Uso interno: upsert. */
  private createDefaultEntry(code: string): TicketClosingFieldStorageEntry {
    return {
      code,
      ticketId: 0,
      closingFieldId: 0,
      closingFieldTypeId: 0,
      value: null,
      remarks: '',
      fileName: '',
      fileContent: '',
    };
  }

  /** Caché en memoria + lectura/parseo de localStorage. Uso interno: getAll/getByCode/upsert. */
  private getEntries(): Map<string, TicketClosingFieldStorageEntry> {
    if (!this.isBrowser) {
      return new Map();
    }

    if (this.inMemoryEntries) {
      return this.inMemoryEntries;
    }

    const storedValue = localStorage.getItem(this.storageKey);

    if (!storedValue) {
      this.inMemoryEntries = new Map();
      return this.inMemoryEntries;
    }

    try {
      const parsedValue = JSON.parse(storedValue) as unknown;
      const entries = Array.isArray(parsedValue)
        ? (parsedValue.filter((item) =>
            this.isStorageEntry(item),
          ) as TicketClosingFieldStorageEntry[])
        : [];

      this.inMemoryEntries = new Map(entries.map((entry) => [entry.code, entry]));
    } catch {
      this.inMemoryEntries = new Map();
    }

    return this.inMemoryEntries;
  }

  /** Actualiza la caché en memoria y escribe en localStorage. Uso interno: upsert. */
  private persist(entries: Map<string, TicketClosingFieldStorageEntry>): void {
    this.inMemoryEntries = entries;

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(Array.from(entries.values())));
    } catch (error) {
      console.warn('No fue posible persistir los campos de cierre en localStorage.', error);
    }
  }

  /** Type guard para filtrar entradas válidas al parsear el JSON de localStorage. Uso interno: getEntries. */
  private isStorageEntry(value: unknown): value is TicketClosingFieldStorageEntry {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const entry = value as Partial<TicketClosingFieldStorageEntry>;
    return typeof entry.code === 'string';
  }
}
